import { Controller } from '@hotwired/stimulus';

/**
 * Confirm Info Modal Controller
 *
 * Intercepts a button/link click to display an HTML informational snippet in a
 * Bootstrap modal. When the user acknowledges, the original click is replayed so
 * any existing handlers (jQuery, React, Turbo, etc.) run normally.
 *
 * Distinct from `turbo-confirm`, which fires a native `confirm()` + Turbo fetch.
 * This controller shows a styled, HTML-content disclosure and resumes the caller's
 * own click handler — no server round-trip.
 *
 * The trigger supplies content via `data-confirm-info-modal-content`. Blank
 * content lets the click pass through untouched.
 *
 * Usage:
 * ```html
 * <div data-controller="confirm-info-modal">
 *   <button data-action="click->confirm-info-modal#intercept:capture"
 *           data-confirm-info-modal-content="<p>Read this first.</p>">
 *     Proceed
 *   </button>
 *
 *   <div class="modal fade" data-confirm-info-modal-target="modal">
 *     <div class="modal-dialog"><div class="modal-content">
 *       <div class="modal-body" data-confirm-info-modal-target="body"></div>
 *       <div class="modal-footer">
 *         <button data-action="click->confirm-info-modal#confirm">Ok</button>
 *       </div>
 *     </div></div>
 *   </div>
 * </div>
 * ```
 *
 * The `:capture` action modifier ensures this handler runs before bubble-phase
 * listeners on the same element, so `stopImmediatePropagation` blocks the
 * underlying click handler until the user acknowledges.
 */
export default class extends Controller {
  static targets = ['modal', 'body'];

  intercept(event) {
    const trigger = event.currentTarget;
    if (trigger.dataset.confirmInfoModalAcknowledged === 'true') return;

    const content = trigger.dataset.confirmInfoModalContent;
    if (!content) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.pendingTrigger = trigger;
    this.bodyTarget.innerHTML = content;
    $(this.modalTarget).modal('show');
  }

  confirm() {
    $(this.modalTarget).modal('hide');
    const trigger = this.pendingTrigger;
    this.pendingTrigger = null;
    if (!trigger) return;

    trigger.dataset.confirmInfoModalAcknowledged = 'true';
    trigger.click();
    delete trigger.dataset.confirmInfoModalAcknowledged;
  }
}
